import { useCallback, useMemo, useRef, useState } from 'react';
import type { Canceler } from 'axios';
import axios from 'axios';
import { debounce } from 'lodash';

import { useCms } from '~/components/hooks/useCms';
import { reportAxiosError } from '~/Utils';

import type { GbgSuburbFindResponse, GbgSuburbSuggestion } from './types';

interface UseGbgSuburbAutocompleteOptions {
  country: string;
  debounceMs?: number;
}

interface UseGbgSuburbAutocompleteReturn {
  suggestions: GbgSuburbSuggestion[];
  isLoading: boolean;
  search: (input: string) => void;
  clearSuggestions: () => void;
}

export const useGbgSuburbAutocomplete = ({
  country,
  debounceMs = 300,
}: UseGbgSuburbAutocompleteOptions): UseGbgSuburbAutocompleteReturn => {
  const { userOrganization } = useCms();
  const [suggestions, setSuggestions] = useState<GbgSuburbSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const cancelRef = useRef<Canceler | null>(null);

  const baseRoute = `/api/v1/organizations/${userOrganization.id}/gbg/suburb`;

  const fetchSuggestions = useCallback(
    async (input: string) => {
      if (!input.trim()) {
        setSuggestions([]);
        return;
      }

      cancelRef.current?.();
      let cancelled = false;

      try {
        setIsLoading(true);
        const { data } = await axios.get<GbgSuburbFindResponse>(`${baseRoute}/find`, {
          params: { locality: input, country },
          cancelToken: new axios.CancelToken((c) => {
            cancelRef.current = c;
          }),
        });
        setSuggestions(data.suggestions);
      } catch (error) {
        cancelled = axios.isCancel(error);
        if (!cancelled) {
          setSuggestions([]);
          // Don't open a dialog for this error, so the user can continue typing manually
          reportAxiosError(error, false);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    },
    [baseRoute, country]
  );

  const debouncedSearch = useMemo(
    () => debounce((input: string) => fetchSuggestions(input), debounceMs),
    [fetchSuggestions, debounceMs]
  );

  const search = useCallback(
    (input: string) => {
      if (!input.trim()) {
        debouncedSearch.cancel();
        setSuggestions([]);
        return;
      }
      debouncedSearch(input);
    },
    [debouncedSearch]
  );

  const clearSuggestions = useCallback(() => {
    debouncedSearch.cancel();
    cancelRef.current?.();
    setSuggestions([]);
    setIsLoading(false);
  }, [debouncedSearch]);

  return {
    suggestions,
    isLoading,
    search,
    clearSuggestions,
  };
};
